import { getInvoicePaymentMethod, getInvoicePaymentType } from '@/shared/constants/fiscalDocuments';
import type {
	EngineActivityItem,
	EngineConcept,
	EngineMovement,
	EngineOverview,
	EnginePeriodDetail,
	EngineRegime,
} from '@/shared/services/declarationsNew.service';
import type {
	AnnualSummary,
	ConceptDeduction,
	CreditNote,
	CycleStateCode,
	ExpenseCategory,
	ForeignCurrency,
	InvoiceConcept,
	InvoiceLine,
	Movement,
	PeriodDetail,
	ReguzPeriod,
	YearMonth,
} from '../model/period';
import type { IconKind } from '../components/StatusIcon';
import type { ActivityEvent } from './activity';
import { displayNameOf } from './counterparty';
import { creditNoteExplanation } from './creditNotes';
import { AI_UNDECIDED_CODE, awaitsUser, PENDING_REVIEW_TEXT } from './deductions';
import { formatPesos, toSentenceCase } from './formatters';
import { NOT_COUNTED_TEXT } from './movementVisuals';
import { buildPeriodMoney, DEFINITIVE_MODALITY_NOTE } from './periodMoney';
import { ATTENTION_ORDER, DECLARABLE_BY_CODE, type DeclarableRegime } from './regimes';

export const declarableOf = (code?: EngineRegime): DeclarableRegime | undefined => (code ? DECLARABLE_BY_CODE[code] : undefined);

const cents = (value: number) => Math.round(value * 100) / 100;

const yearOf = (overview: EngineOverview | undefined, year: number) => overview?.years.find((candidate) => candidate.year === year);

export const yearMonthsOf = (overview: EngineOverview | undefined, year: number): YearMonth[] =>
	(yearOf(overview, year)?.months ?? []).map(
		({ period, state, result, total, income, regime, regimes, cells, payroll, pastDue, receiptChecked }) => ({
			period,
			regime,
			state,
			result,
			total,
			income,
			regimes,
			cells,
			payroll,
			pastDue,
			receiptChecked,
		}),
	);

export const unconfirmedCountOf = (overview: EngineOverview | undefined) => overview?.unconfirmedCount ?? 0;

export const isSalaryYear = (overview: EngineOverview | undefined, year: number) => yearOf(overview, year)?.kind === 'sueldos';

export const needsLinking = (overview: EngineOverview | undefined) => overview?.sync === 'sin_perfil' || overview?.sync === 'reconectar';

export const obligatedMonthsOf = (overview: EngineOverview | undefined, year: number) => yearOf(overview, year)?.obligatedMonths;

export const annualSummaryOf = (overview: EngineOverview | undefined, year: number): AnnualSummary | undefined =>
	yearOf(overview, year)?.annual;

export const yearRangeOf = (overview: EngineOverview | undefined) => {
	const years = overview?.years.map((candidate) => candidate.year) ?? [];
	return years.length > 0 ? { min: Math.min(...years), max: Math.max(...years) } : undefined;
};

export const mainRegimeOf = (overview: EngineOverview | undefined, period: string): DeclarableRegime | undefined =>
	declarableOf(yearOf(overview, Number(period.slice(0, 4)))?.months.find((month) => month.period === period)?.regime);

export const attentionPeriodOf = (overview: EngineOverview | undefined, year: number) => {
	const started = yearMonthsOf(overview, year).filter((month): month is YearMonth & { state: CycleStateCode } => month.state !== null);
	const [first] = [...started].sort(
		(a, b) => ATTENTION_ORDER.indexOf(a.state) - ATTENTION_ORDER.indexOf(b.state) || b.period.localeCompare(a.period),
	);
	return first?.period ?? null;
};

export const catchUpPeriodsOf = (overview: EngineOverview | undefined): (ReguzPeriod & { regime?: DeclarableRegime })[] =>
	(overview?.catchUp ?? []).map(({ period, state, result, total, surcharges, regime, hadRegime }) => ({
		period,
		state,
		result,
		total,
		surcharges,
		hadRegime,
		regime: declarableOf(regime),
	}));

const REASONS: Record<string, string> = {
	uso_personal: 'Es un gasto personal',
	sin_relacion: 'No tiene relación con tu trabajo',
	descripcion_insuficiente: 'No pudimos identificar el gasto',
	centavos: 'Es un ajuste de centavos',
	modalidad_definitiva: 'En pago definitivo no se restan gastos',
	gasolina_efectivo: 'La gasolina pagada en efectivo no se deduce',
	no_bancarizado: 'Se pagó en efectivo por más de $2,000',
	forma_pago_sin_flujo: 'Su forma de pago no es un pago real',
	deduccion_personal: 'Es deducción personal: va en tu declaración anual',
	catalogo_actividad: 'No se relaciona con tu actividad',
	curada_actividad: 'No se relaciona con tu actividad',
	catalogo_emisor: 'Quien te factura no se relaciona con tu actividad',
	coherencia_familia: 'No corresponde a tus gastos de trabajo',
	historial: 'Antes no la contamos como gasto de tu trabajo',
	'No deducible, PPD': 'Falta el comprobante de pago',
	'No deducible, uso CFDI': 'El uso del CFDI no es de gasto',
	'No aplica por forma de pago': 'Su forma de pago no permite deducirla',
	'No aplica, regla de centavos': 'Es un ajuste de centavos',
	pendiente: PENDING_REVIEW_TEXT,
	[AI_UNDECIDED_CODE]: 'No pudimos clasificarlo',
	contador: 'Así lo decidió tu contador',
};

const UNDECIDED_CODES = new Set(['pendiente', 'descripcion_insuficiente', AI_UNDECIDED_CODE]);

const LAW_EXPLANATIONS: Record<string, (movement: EngineMovement) => string> = {
	gasolina_efectivo: () =>
		'No la contamos: la gasolina pagada en efectivo no se deduce por ley, aunque sea para tu trabajo. La próxima vez págala con tarjeta o transferencia y sí la contamos.',
	no_bancarizado: () =>
		'No la contamos: arriba de $2,000 la ley pide pagar con tarjeta, transferencia o cheque. La próxima vez págala así y sí la contamos.',
	forma_pago_sin_flujo: (movement) =>
		`No la contamos: la factura dice que no se pagó con dinero${movement.paymentForm ? ` (forma de pago ${getInvoicePaymentMethod(movement.paymentForm)})` : ''}. Pide al proveedor que la corrija.`,
	deduccion_personal: () => 'Es deducción personal: la usamos en tu declaración anual, no en la mensual.',
	modalidad_definitiva: () => 'Con retención definitiva la plataforma ya cubre tus impuestos; tus gastos no se deducen este mes.',
};

const SOURCES: Record<string, NonNullable<ConceptDeduction['source']>> = { rule: 'rule', ai: 'ai', user: 'user', contador: 'contador' };

const CATEGORY_PREFIXES: [string, ExpenseCategory][] = [
	['78181501', 'lavado'],
	['151015', 'gasolina'],
	['251725', 'llantas'],
	['2517', 'refacciones'],
	['781815', 'mantenimiento'],
	['8311', 'telefonia'],
	['811120', 'rastreo'],
	['5310', 'ropa'],
	['5019', 'comida'],
	['9010', 'comida'],
	['9015', 'entretenimiento'],
];

const categoryOf = (concepts: EngineConcept[]): ExpenseCategory | undefined => {
	const code = concepts.find((concept) => concept.productCode)?.productCode;
	return code ? CATEGORY_PREFIXES.find(([prefix]) => code.startsWith(prefix))?.[1] : undefined;
};

const lowerFirst = (text: string) => text.charAt(0).toLowerCase() + text.slice(1);

const foreignOf = (movement: EngineMovement): ForeignCurrency | undefined =>
	movement.currency === 'USD' && movement.exchangeRate
		? { code: 'USD', amount: cents((movement.subtotal + movement.iva) / movement.exchangeRate), rate: movement.exchangeRate }
		: undefined;

const conceptOf = (concept: EngineConcept, expense: boolean, rate?: number): InvoiceConcept => {
	const reason = concept.reasonCode ? REASONS[concept.reasonCode] : undefined;
	return {
		productCode: concept.productCode,
		productName: concept.productName && toSentenceCase(concept.productName),
		description: toSentenceCase(concept.description || concept.productName || 'Concepto'),
		quantity: concept.quantity,
		unit: concept.unit,
		unitPrice: concept.unitPrice,
		amount: rate ? cents(concept.amount / rate) : concept.amount,
		iva: rate ? cents(concept.iva / rate) : concept.iva,
		...(expense
			? {
					deduction: {
						deductible: concept.deductible ?? false,
						code: concept.reasonCode,
						pending: !concept.deductible && (concept.pending || UNDECIDED_CODES.has(concept.reasonCode ?? '')),
						reason: concept.deductible ? undefined : reason,
						overridable: concept.overridable,
						markedByUser: concept.source === 'user' && Boolean(concept.deductible),
						conceptId: concept.id,
						source: concept.source ? SOURCES[concept.source] : undefined,
					},
				}
			: {}),
	};
};

const invoiceLinesOf = (movement: EngineMovement, name: string, foreign: boolean): InvoiceLine[] => {
	const suffix = foreign ? ' en pesos' : '';
	if (movement.docType === 'Constancia de retenciones') {
		return [
			{ label: 'Cobraste', amount: movement.subtotal },
			...(movement.iva > 0 ? [{ label: 'IVA de lo que cobraste', amount: movement.iva }] : []),
			...(movement.isrWithheld > 0 ? [{ label: `${name} te retuvo de ISR`, amount: -movement.isrWithheld }] : []),
			...(movement.ivaWithheld > 0 ? [{ label: `${name} te retuvo de IVA`, amount: -movement.ivaWithheld }] : []),
		];
	}
	return [
		{ label: `Subtotal${suffix}`, amount: movement.subtotal },
		...(movement.iva !== 0 ? [{ label: `IVA${suffix}`, amount: movement.iva }] : []),
		...(movement.isrWithheld > 0 ? [{ label: `Te retuvieron de ISR${suffix}`, amount: -movement.isrWithheld }] : []),
		...(movement.ivaWithheld > 0 ? [{ label: `Te retuvieron de IVA${suffix}`, amount: -movement.ivaWithheld }] : []),
		{
			label: `Total${suffix}`,
			amount: cents(movement.subtotal + movement.iva - movement.isrWithheld - movement.ivaWithheld),
			strong: true,
		},
	];
};

const creditNoteOf = (note: NonNullable<EngineMovement['creditNote']>): CreditNote => ({
	side: note.side,
	adjusts: note.adjusts,
	adjustsOn: note.adjustsOn,
	effect: note.effect,
	reason: note.reason,
	deductible: note.deductible ?? 0,
	iva: note.iva ?? 0,
});

const explanationOf = (movement: EngineMovement, name: string, concepts: InvoiceConcept[]) => {
	if (movement.notCounted && !movement.creditNote) return NOT_COUNTED_TEXT;
	if (movement.creditNote) return creditNoteExplanation(movement.amount, creditNoteOf(movement.creditNote), name);
	if (movement.docType === 'Constancia de retenciones') {
		const withheld = movement.isrWithheld + movement.ivaWithheld;
		return withheld > 0
			? `${name} ya le pagó al SAT ${formatPesos(withheld)} por ti entre ISR e IVA. Lo restamos de tus impuestos del mes.`
			: `Es lo que te pagó ${name} en el mes.`;
	}
	if (movement.kind === 'ingreso') return 'Es lo que cobraste con esta factura. Su IVA y su ISR van en tu declaración.';
	if (movement.kind === 'deducible') {
		const partial = movement.concepts.some((concept) => concept.deductible === false);
		const deductibleIva = cents(movement.concepts.reduce((sum, concept) => sum + (concept.deductible ? concept.iva : 0), 0));
		if (deductibleIva <= 0) return partial ? 'Una parte es gasto deducible.' : 'Es un gasto deducible.';
		return partial
			? `Una parte es gasto deducible. Restamos el IVA de esa parte, ${formatPesos(deductibleIva)}, del que cobraste.`
			: `Es un gasto deducible. Restamos su IVA de ${formatPesos(deductibleIva)} del que cobraste.`;
	}
	const lawCode = movement.concepts.find(
		(concept) => !concept.deductible && concept.reasonCode && LAW_EXPLANATIONS[concept.reasonCode],
	)?.reasonCode;
	if (lawCode) return LAW_EXPLANATIONS[lawCode](movement);
	if (concepts.length > 0 && concepts.every((concept) => concept.deduction?.pending)) {
		if (concepts.some(awaitsUser)) return 'No sabemos si este gasto es para tu trabajo.';
		return 'Todavía no decidimos si es deducible. En cuanto lo revisemos te decimos aquí.';
	}
	const reason = concepts.find((concept) => concept.deduction && !concept.deduction.deductible)?.deduction?.reason;
	return reason ? `No la contamos: ${lowerFirst(reason)}.` : 'No la contamos como gasto de tu trabajo.';
};

export const movementOf = (movement: EngineMovement): Movement => {
	const name = movement.name.trim() || 'Sin nombre';
	const shownName = displayNameOf(name);
	const expense = movement.kind !== 'ingreso' && !movement.creditNote;
	const foreignCurrency = foreignOf(movement);
	const concepts = movement.concepts.map((concept) => conceptOf(concept, expense, foreignCurrency?.rate));
	return {
		id: movement.id,
		kind: movement.kind,
		name,
		concept: toSentenceCase(movement.concept),
		date: movement.date,
		amount: movement.amount,
		category: expense ? categoryOf(movement.concepts) : undefined,
		explanation: explanationOf(movement, shownName, concepts),
		creditNote: movement.creditNote && creditNoteOf(movement.creditNote),
		notCounted: movement.notCounted,
		invoice: {
			docType: movement.docType,
			rfc: movement.counterpartyRfc,
			uuid: movement.uuid,
			paymentForm: movement.paymentForm ? getInvoicePaymentMethod(movement.paymentForm) : undefined,
			paymentMethod: movement.paymentMethod ? getInvoicePaymentType(movement.paymentMethod) : undefined,
			foreignCurrency,
			issuedByHeru: movement.issuedByHeru,
			concepts: concepts.length > 0 ? concepts : undefined,
			lines: invoiceLinesOf(movement, shownName, foreignCurrency !== undefined),
		},
	};
};

const USER_ACTION_ICONS: Record<NonNullable<EngineActivityItem['action']>, IconKind> = {
	aprobar: 'presentando',
	objetar: 'en_revision',
	pago_reportado: 'por_pagar',
	nueva_linea: 'generando_linea',
};

const activityIconOf = (item: EngineActivityItem): IconKind => {
	switch (item.kind) {
		case 'pago_registrado':
			return 'pagada';
		case 'lista_para_aprobar':
			return 'listo_para_aprobar';
		case 'declaracion_sin_efecto':
			return 'sin_presentar';
		case 'linea_nueva':
			return 'por_pagar';
		case 'efectivo_reportado':
			return 'ingreso';
		case 'revision_respondida':
			return 'revision_respondida';
		case 'accion_no_completada':
			return 'en_revision';
		case 'accion_usuario':
			return item.action ? USER_ACTION_ICONS[item.action] : 'presentando';
		default:
			return item.result === 'saldo_a_favor' ? 'saldo_a_favor' : 'presentada';
	}
};

export const activityEventOf = (item: EngineActivityItem): ActivityEvent | null =>
	item.kind === 'factura' || !item.title
		? null
		: {
				type: 'evento',
				id: item.id,
				at: item.at,
				kind: activityIconOf(item),
				title: item.title,
				detail: item.detail,
				amount: item.amount,
				period: item.period,
				regime: declarableOf(item.regime),
				year: item.year,
			};

export const periodDetailOf = (detail: EnginePeriodDetail): PeriodDetail => ({
	period: detail.period,
	regime: detail.regime,
	regimeName: detail.regimeName,
	regimes: detail.regimes,
	lane: detail.lane,
	state: detail.state,
	result: detail.result,
	total: detail.total,
	receiptChecked: detail.receiptChecked,
	forecast: detail.forecast,
	paymentDeadline: detail.paymentDeadline,
	request: detail.request,
	money: detail.money
		? buildPeriodMoney(
				detail.money.movements.map(movementOf),
				detail.money.taxLines,
				detail.money.definitive ? DEFINITIVE_MODALITY_NOTE : undefined,
			)
		: null,
	signedBy: detail.signedBy,
	submissionStep: detail.submissionStep,
	presentedOn: detail.presentedOn,
	operationNumber: detail.operationNumber,
	documents: detail.documents,
	captureLine: detail.captureLine,
	newLine: detail.state === 'por_pagar' ? detail.newLine : undefined,
	payment: detail.payment,
	priorPayment: detail.state === 'por_pagar' ? detail.priorPayment : undefined,
	block: detail.block,
	locked: detail.locked,
	purchase: detail.purchase,
	review: detail.review && {
		reason: detail.review.reason,
		comment: detail.review.comment,
		reportedAt: detail.review.reportedAt,
		answer: detail.review.answer && { message: detail.review.answer.message, at: detail.review.answer.at },
	},
	surcharges: detail.surcharges,
	paymentCheck: detail.paymentCheck,
	recalculating: detail.recalculating,
	askCash: detail.askCash,
	needs: detail.needs,
	notice: detail.notice,
	sourceCheck: detail.sourceCheck,
	approvalHeld: detail.approvalHeld,
	voided: detail.voided,
	history: (detail.history ?? []).flatMap((item) => activityEventOf(item) ?? []),
	capabilities: detail.capabilities,
});
